'use client'

import AutoScroll from 'embla-carousel-auto-scroll'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from '@mdxui/primitives'
import { cn } from '../../lib/cn'

/**
 * A logo item can be either an image or text string
 */
export type LogoItem =
  | {
      /** Image source URL */
      src: string
      /** Alt text for the image */
      alt: string
      /** Optional link URL */
      href?: string
    }
  | string

/**
 * Props for LogosCarousel component
 *
 * @remarks
 * **IMPORTANT CONSTRAINTS FOR AI/PROGRAMMATIC GENERATION:**
 * - Use semantic presets: `LogosSection` (bordered) or `LogosInline` (borderless)
 * - Logos can be image objects `{ src, alt, href? }` OR text strings like `"workers.do"`
 * - Text logos render as semibold text, image logos auto-invert in dark mode
 * - Both presets accept optional `title` (h2) or `description` (uppercase mono label)
 * - LogosSection: Standalone section between blocks (Hero → Logos → Features)
 * - LogosInline: Embedded inside Hero, CTA, or other components
 */
export interface LogosProps {
  /** Array of logos - can be image objects or text strings */
  logos?: LogoItem[]
  /** Optional title (h2) above the carousel */
  title?: string
  /** Optional description - renders as small uppercase mono text */
  description?: string
  /** Show top/bottom borders on carousel items (default: true) */
  bordered?: boolean
  /** Additional class name */
  className?: string
  /** Auto-scroll speed (default: 1) */
  speed?: number
  /** Scroll direction */
  direction?: 'forward' | 'backward'
}

const defaultLogos: LogoItem[] = [
  { src: '/logos/mdxui.svg', alt: 'MDXUI' },
  'workers.do',
  'functions.do',
  'apis.do',
  'database.do',
  'llm.do',
  'agents.do',
  'workflows.do',
]

/**
 * LogosCarousel - Auto-scrolling logo carousel with edge fade effects
 *
 * **DO NOT USE DIRECTLY** - Use semantic presets instead:
 * - `LogosSection` - Bordered, for standalone sections between blocks
 * - `LogosInline` - Borderless, for embedding inside Hero/CTA
 *
 * Both presets accept optional `title` and `description` props.
 *
 * **Layout:**
 * ```
 * ┌────────────────────────────────────────────────────────────┐
 * │  [fade]  │ Logo │ Logo │ Logo │ Logo │ Logo │  [fade]     │
 * └────────────────────────────────────────────────────────────┘
 * ```
 *
 * **Logo Types:**
 * - Image: `{ src: '/logos/acme.svg', alt: 'Acme', href?: 'https://acme.com' }`
 * - Text: `'workers.do'` - renders as semibold text
 *
 * @example
 * ```tsx
 * // Standalone section with title
 * <LogosSection
 *   title="Trusted by Industry Leaders"
 *   logos={[{ src: '/logos/acme.svg', alt: 'Acme' }, 'workers.do']}
 * />
 *
 * // Standalone section with description label
 * <LogosSection
 *   description="Built with"
 *   logos={['workers.do', 'functions.do', 'apis.do']}
 * />
 *
 * // Embedded in Hero/CTA (no borders)
 * <LogosInline logos={[{ src: '/logos/acme.svg', alt: 'Acme' }]} />
 * ```
 */
const LogosCarousel = ({
  logos = defaultLogos,
  title,
  description,
  bordered = true,
  className,
  speed = 1,
  direction = 'forward',
}: LogosProps) => {
  // Duplicate logos to ensure smooth continuous scrolling
  // The carousel needs more items than visible to create seamless loop
  const duplicatedLogos = [...logos, ...logos]

  return (
    <section className={cn('relative bg-background py-32', className)}>
      {/* Content wrapper constrained to max-w-7xl */}
      <div className="relative mx-auto max-w-7xl">
        {/* Edge fade effects - positioned at max-w-7xl container edges */}
        <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-32 bg-linear-to-r from-background to-transparent" />
        <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-32 bg-linear-to-l from-background to-transparent" />

        {/* Title or description */}
        {(title || description) && (
          <div className="mb-8 text-center">
            {description && (
              <p className="text-xs uppercase tracking-widest text-muted-foreground font-mono">
                {description}
              </p>
            )}
            {title && (
              <h2 className="mt-2 text-2xl font-semibold sm:text-3xl">{title}</h2>
            )}
          </div>
        )}

        {/* Carousel - overflow-hidden clips logos at container edges */}
        <div className="overflow-hidden pt-6 sm:pt-8">
          <Carousel
            opts={{ loop: true }}
            plugins={[
              AutoScroll({
                playOnInit: true,
                speed,
                direction,
              }),
            ]}
          >
            <CarouselContent className="ml-0">
              {duplicatedLogos.map((logo, index) => {
                const isText = typeof logo === 'string'
                const key = isText ? logo : logo.alt

                return (
                  <CarouselItem
                    key={`${key}-${index}`}
                    className={cn(
                      'relative mx-4 flex h-24 basis-1/2 items-center justify-center pl-0 sm:basis-1/4 md:basis-1/5 lg:basis-1/6',
                      bordered && 'border-y border-border'
                    )}
                  >
                    <div className="flex flex-col items-center justify-center lg:mx-10">
                      {isText ? (
                        <span className="text-base font-medium text-foreground whitespace-nowrap">
                          {logo}
                        </span>
                      ) : logo.href ? (
                        <a
                          href={logo.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="transition-opacity hover:opacity-80"
                        >
                          <img
                            src={logo.src}
                            alt={logo.alt}
                            className="h-7 w-auto dark:invert"
                          />
                        </a>
                      ) : (
                        <img
                          src={logo.src}
                          alt={logo.alt}
                          className="h-7 w-auto dark:invert"
                        />
                      )}
                    </div>
                  </CarouselItem>
                )
              })}
            </CarouselContent>
          </Carousel>
        </div>
      </div>
    </section>
  )
}

export { LogosCarousel }

/**
 * LogosSection - Standalone logo carousel section
 *
 * Use for: Trust signals between blocks (Hero → Logos → Features)
 * - Bordered carousel items for visual separation
 * - Optional title (h2) or description (uppercase mono label)
 */
export const LogosSection = (props: Omit<LogosProps, 'bordered'>) => (
  <LogosCarousel {...props} bordered={true} />
)

/**
 * LogosInline - Embedded logo carousel
 *
 * Use for: Logos inside Hero, CTA, or other components
 * - No borders for seamless integration
 * - Optional title or description still supported
 */
export const LogosInline = (props: Omit<LogosProps, 'bordered'>) => (
  <LogosCarousel {...props} bordered={false} />
)
